import {
  Box,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { JobDetailsResponse } from '../../api/contracts/JobsResponse';
import { formatDate } from '../../utils/formatDate';

type RetailersStatusListProps = {
  job: JobDetailsResponse;
};

export const RetailersStatusList = ({ job }: RetailersStatusListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      spacing={isMobile ? 2 : 1}
      sx={{
        mt: isMobile ? 1 : 2,
        flexWrap: 'wrap',
        gap: isMobile ? 2 : 1,
      }}
    >
      {job.retailers.map((retailerStats) => {
        const {
          retailer,
          status,
          productsTotal,
          productsProcessed,
          productsFailed,
          productsAvailable,
          productsUnavailable,
          resumeAt,
        } = retailerStats;

        let color: 'default' | 'success' | 'error' | 'warning' = 'default';

        switch (status) {
          case 'completed':
            color = 'success';
            break;
          case 'failed':
            color = 'error';
            break;
          case 'processing':
            color = 'warning';
            break;
          default:
            color = 'default';
        }

        return (
          <Box
            key={retailer}
            sx={{
              minWidth: isMobile ? '100%' : 120,
              maxWidth: isMobile ? '100%' : 200,
              flex: isMobile ? '1 1 100%' : '0 1 auto',
            }}
          >
            <Chip
              label={retailer}
              size="small"
              variant="outlined"
              color={color}
              sx={{
                marginBottom: '5px',
                width: 'auto',
              }}
            />
            {productsTotal > 0 && (
              <Box sx={{ width: '100%', mt: 0.5 }}>
                <Box
                  sx={{
                    display: 'flex',
                    height: 4,
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  {productsAvailable > 0 && (
                    <Box
                      sx={{
                        width: `${(productsAvailable / productsTotal) * 100}%`,
                        bgcolor: 'success.main',
                      }}
                    />
                  )}
                  {productsUnavailable > 0 && (
                    <Box
                      sx={{
                        width: `${(productsUnavailable / productsTotal) * 100}%`,
                        bgcolor: 'warning.main',
                      }}
                    />
                  )}
                  {productsFailed > 0 && (
                    <Box
                      sx={{
                        width: `${(productsFailed / productsTotal) * 100}%`,
                        bgcolor: 'error.main',
                      }}
                    />
                  )}
                  {productsProcessed < productsTotal && (
                    <Box
                      sx={{
                        width: `${((productsTotal - productsProcessed - productsFailed) / productsTotal) * 100}%`,
                        bgcolor: 'grey.300',
                      }}
                    />
                  )}
                </Box>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    processed: {productsProcessed} / {productsTotal} (
                    {((productsProcessed / productsTotal) * 100).toFixed()}%)
                  </Typography>
                  {productsAvailable > 0 && (
                    <Typography variant="caption" color="success.main">
                      available: {productsAvailable} (
                      {(
                        (productsAvailable / productsProcessed) *
                        100
                      ).toFixed()}
                      %)
                    </Typography>
                  )}
                  {productsUnavailable > 0 && (
                    <Typography variant="caption" color="warning.main">
                      unavailable: {productsUnavailable} (
                      {(
                        (productsUnavailable / productsProcessed) *
                        100
                      ).toFixed()}
                      %)
                    </Typography>
                  )}
                  {productsFailed > 0 && (
                    <Typography variant="caption" color="error">
                      failed: {productsFailed} (
                      {productsFailed + productsProcessed > 0
                        ? (
                            (productsFailed /
                              (productsFailed + productsProcessed)) *
                            100
                          ).toFixed()
                        : 0}
                      %)
                    </Typography>
                  )}
                  {resumeAt && (
                    <Typography variant="caption" color="text.secondary">
                      resume at: {formatDate(resumeAt)}
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
};
